import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, Globe, Search, MapPin, Linkedin, Instagram, ExternalLink,
  UserPlus, Check, X, MessageSquare, Star, Crown, Filter
} from 'lucide-react';

const EXPERTISE_OPTIONS = [
  'Branding', 'Marketing', 'Coaching', 'Design', 'Writing', 
  'Social Media', 'Strategy', 'Finance', 'Tech', 'Wellness', 'E-commerce'
];

const LOOKING_FOR_OPTIONS = [
  'Collaborations', 'Networking', 'Clients', 'Mentorship', 
  'Partnerships', 'Learning', 'Community', 'Accountability'
];

const WomenConnect = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [connections, setConnections] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [mapData, setMapData] = useState({ locations: {}, countries: {} });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExpertise, setFilterExpertise] = useState('');
  const [showFeatured, setShowFeatured] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    tagline: '', expertise: [], looking_for: [], location: '',
    linkedin_url: '', instagram_handle: '', website_url: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterExpertise, showFeatured]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [membersRes, profileRes, connectionsRes, requestsRes, mapRes] = await Promise.all([
        axios.get(`${API}/women-connect/members`, { 
          params: { expertise: filterExpertise || undefined, featured: showFeatured || undefined },
          headers, 
           
        }),
        axios.get(`${API}/women-connect/profile`, { headers }),
        axios.get(`${API}/women-connect/connections`, { headers }),
        axios.get(`${API}/women-connect/requests`, { headers }),
        axios.get(`${API}/women-connect/map`, { headers })
      ]);
      
      setMembers(membersRes.data.members);
      setMyProfile(profileRes.data.profile);
      setConnections(connectionsRes.data.connections);
      setRequests(requestsRes.data);
      setMapData(mapRes.data);
      
      if (profileRes.data.profile) {
        setProfileForm(profileRes.data.profile);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveProfile = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/women-connect/profile`, profileForm, { headers });
      toast.success('Profile updated!');
      setShowProfileModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const sendConnectionRequest = async (toUserId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/women-connect/request`, { to_user_id: toUserId }, { headers });
      toast.success('Connection request sent!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const respondToRequest = async (requestId, action) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/women-connect/requests/${requestId}/respond`, { action }, { headers });
      toast.success(`Request ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to respond');
    }
  };

  const toggleExpertise = (exp) => {
    const current = profileForm.expertise || [];
    if (current.includes(exp)) {
      setProfileForm({ ...profileForm, expertise: current.filter(e => e !== exp) });
    } else if (current.length < 3) {
      setProfileForm({ ...profileForm, expertise: [...current, exp] });
    }
  };

  const toggleLookingFor = (item) => {
    const current = profileForm.looking_for || [];
    if (current.includes(item)) {
      setProfileForm({ ...profileForm, looking_for: current.filter(l => l !== item) });
    } else if (current.length < 3) {
      setProfileForm({ ...profileForm, looking_for: [...current, item] });
    }
  };

  const isConnected = (userId) => connections.some(c => c.user_id === userId);
  const hasPendingRequest = (userId) => requests.outgoing.some(r => r.to_user_id === userId && r.status === 'pending');

  return (
    <DashboardLayout title="Women Connect">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/20 to-transparent rounded-full blur-3xl" />
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">CLIERXTER GLOBAL WOMEN COMMUNITY</span>
              </div>
              
              <h2 className="font-serif text-3xl text-white mb-2">Connect. Collaborate. Grow.</h2>
              <p className="text-[#A1A1AA] font-light">
                Network with visionary women entrepreneurs worldwide.
              </p>
            </div>

            <Button onClick={() => setShowProfileModal(true)} className="btn-gold px-6 py-3" data-testid="edit-profile-btn">
              {myProfile ? 'Edit Profile' : 'Create Profile'}
            </Button>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#1C1C1C] pb-4">
          {[
            { id: 'members', label: 'Members', icon: Users },
            { id: 'connections', label: 'My Connections', icon: UserPlus },
            { id: 'requests', label: 'Requests', icon: MessageSquare },
            { id: 'map', label: 'Global Map', icon: Globe }
          ].map(tab => {
            const Icon = tab.icon;
            const count = tab.id === 'requests' ? requests.incoming.length : 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 flex items-center gap-2 transition-all relative ${
                  activeTab === tab.id
                    ? 'text-[#E5C585] bg-[#E5C585]/5 border-b-2 border-[#D4AF37]'
                    : 'text-[#71717A] hover:text-white'
                }`}
              >
                <Icon size={18} />
                {tab.label}
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-black text-xs flex items-center justify-center rounded-full">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Members View */}
        {activeTab === 'members' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Filters */}
            <div className="card-premium p-4 mb-6 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={18} />
                  <Input
                    placeholder="Search members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-transparent border-[#27272A]"
                  />
                </div>
              </div>
              
              <select
                value={filterExpertise}
                onChange={(e) => setFilterExpertise(e.target.value)}
                className="bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] px-4 py-2 text-sm"
              >
                <option value="">All Expertise</option>
                {EXPERTISE_OPTIONS.map(exp => (
                  <option key={exp} value={exp}>{exp}</option>
                ))}
              </select>

              <button
                onClick={() => setShowFeatured(!showFeatured)}
                className={`px-4 py-2 flex items-center gap-2 text-sm transition-all ${
                  showFeatured ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]' : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA]'
                }`}
              >
                <Crown size={16} />
                Featured Only
              </button>
            </div>

            {/* Members Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {members.filter(m => 
                !searchQuery || 
                m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                m.tagline?.toLowerCase().includes(searchQuery.toLowerCase())
              ).map((member, index) => (
                <motion.div
                  key={member.user_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`card-premium p-6 ${member.is_featured ? 'border-[#D4AF37]/30' : ''}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    {member.picture ? (
                      <img src={member.picture} alt={member.name} className="w-14 h-14 rounded-full border border-[#27272A]" />
                    ) : (
                      <div className="w-14 h-14 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                        <span className="text-[#E5C585] font-serif text-lg">{member.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-medium">{member.name}</h4>
                        {member.is_featured && <Crown className="text-[#D4AF37]" size={14} />}
                      </div>
                      <p className="text-[#A1A1AA] text-sm">{member.tagline}</p>
                    </div>
                  </div>

                  {member.expertise && member.expertise.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {member.expertise.map(exp => (
                        <span key={exp} className="text-[10px] px-2 py-1 bg-[#D4AF37]/10 text-[#E5C585] border border-[#D4AF37]/20">
                          {exp}
                        </span>
                      ))}
                    </div>
                  )}

                  {member.location && (
                    <div className="flex items-center gap-2 text-[#52525B] text-sm mb-4">
                      <MapPin size={14} />
                      {member.location}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {member.linkedin_url && (
                      <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] hover:text-[#E5C585] transition-colors">
                        <Linkedin size={16} />
                      </a>
                    )}
                    {member.instagram_handle && (
                      <a href={`https://instagram.com/${member.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] hover:text-[#E5C585] transition-colors">
                        <Instagram size={16} />
                      </a>
                    )}
                    {member.website_url && (
                      <a href={member.website_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] hover:text-[#E5C585] transition-colors">
                        <ExternalLink size={16} />
                      </a>
                    )}
                    
                    <div className="flex-1" />
                    
                    {member.user_id !== user?.user_id && (
                      isConnected(member.user_id) ? (
                        <span className="text-[#52525B] text-xs flex items-center gap-1">
                          <Check size={14} /> Connected
                        </span>
                      ) : hasPendingRequest(member.user_id) ? (
                        <span className="text-[#D4AF37] text-xs">Pending</span>
                      ) : (
                        <Button onClick={() => sendConnectionRequest(member.user_id)} className="btn-outline-gold px-3 py-1.5 text-xs">
                          <UserPlus size={14} className="mr-1" />
                          Connect
                        </Button>
                      )
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Connections View */}
        {activeTab === 'connections' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {connections.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {connections.map((conn, index) => (
                  <motion.div
                    key={conn.user_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="card-premium p-6"
                  >
                    <div className="flex items-center gap-4">
                      {conn.picture ? (
                        <img src={conn.picture} alt={conn.name} className="w-12 h-12 rounded-full" />
                      ) : (
                        <div className="w-12 h-12 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                          <span className="text-[#E5C585] font-serif">{conn.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <h4 className="text-white">{conn.name}</h4>
                        <p className="text-[#52525B] text-sm">{conn.tagline}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="card-premium p-16 text-center">
                <Users className="text-[#27272A] mx-auto mb-4" size={60} />
                <h3 className="font-serif text-xl text-white mb-2">No Connections Yet</h3>
                <p className="text-[#52525B]">Browse members and start connecting!</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Requests View */}
        {activeTab === 'requests' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            {/* Incoming */}
            <div>
              <h3 className="font-serif text-lg text-white mb-4">Incoming Requests</h3>
              {requests.incoming.length > 0 ? (
                <div className="space-y-3">
                  {requests.incoming.map(req => (
                    <div key={req.request_id} className="card-premium p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                          <span className="text-[#E5C585] font-serif text-sm">{req.from_user_name?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white">{req.from_user_name}</p>
                          {req.message && <p className="text-[#52525B] text-sm">{req.message}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => respondToRequest(req.request_id, 'accept')} className="btn-gold px-4 py-2 text-sm">
                          <Check size={14} className="mr-1" />Accept
                        </Button>
                        <Button onClick={() => respondToRequest(req.request_id, 'decline')} className="btn-outline-gold px-4 py-2 text-sm">
                          <X size={14} className="mr-1" />Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#52525B]">No pending requests</p>
              )}
            </div>

            {/* Outgoing */}
            <div>
              <h3 className="font-serif text-lg text-white mb-4">Sent Requests</h3>
              {requests.outgoing.length > 0 ? (
                <div className="space-y-3">
                  {requests.outgoing.map(req => (
                    <div key={req.request_id} className="card-luxury p-4 flex items-center justify-between">
                      <span className="text-[#A1A1AA]">Request to {req.to_user_id}</span>
                      <span className={`text-xs px-2 py-1 ${
                        req.status === 'pending' ? 'text-[#D4AF37] bg-[#D4AF37]/10' :
                        req.status === 'accepted' ? 'text-green-400 bg-green-400/10' :
                        'text-red-400 bg-red-400/10'
                      }`}>
                        {req.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#52525B]">No sent requests</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Map View */}
        {activeTab === 'map' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card-premium p-8">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="text-[#D4AF37]" size={24} />
                <h3 className="font-serif text-xl text-white">Global Member Map</h3>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(mapData.countries).map(([country, count]) => (
                  <div key={country} className="p-4 bg-[#0A0A0A] border border-[#27272A]">
                    <div className="flex items-center justify-between">
                      <span className="text-white">{country}</span>
                      <span className="text-[#D4AF37]">{count} members</span>
                    </div>
                  </div>
                ))}
              </div>

              {Object.keys(mapData.countries).length === 0 && (
                <p className="text-center text-[#52525B]">No location data yet. Update your profile to appear on the map!</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Profile Modal */}
        {showProfileModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-luxury p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <h3 className="font-serif text-2xl text-white mb-6">Your Networking Profile</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Tagline</label>
                  <Input
                    value={profileForm.tagline}
                    onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })}
                    placeholder="e.g., Brand Strategist helping women build luxury brands"
                    className="input-underline"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Expertise (Select up to 3)</label>
                  <div className="flex flex-wrap gap-2">
                    {EXPERTISE_OPTIONS.map(exp => (
                      <button
                        key={exp}
                        onClick={() => toggleExpertise(exp)}
                        className={`px-3 py-1.5 text-sm transition-all ${
                          (profileForm.expertise || []).includes(exp)
                            ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]'
                            : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA]'
                        }`}
                      >
                        {exp}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Looking For (Select up to 3)</label>
                  <div className="flex flex-wrap gap-2">
                    {LOOKING_FOR_OPTIONS.map(item => (
                      <button
                        key={item}
                        onClick={() => toggleLookingFor(item)}
                        className={`px-3 py-1.5 text-sm transition-all ${
                          (profileForm.looking_for || []).includes(item)
                            ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]'
                            : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA]'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Location</label>
                  <Input
                    value={profileForm.location || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                    placeholder="City, Country"
                    className="input-underline"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">LinkedIn URL</label>
                    <Input
                      value={profileForm.linkedin_url || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, linkedin_url: e.target.value })}
                      placeholder="https://linkedin.com/in/..."
                      className="input-underline"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Instagram Handle</label>
                    <Input
                      value={profileForm.instagram_handle || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, instagram_handle: e.target.value })}
                      placeholder="@yourhandle"
                      className="input-underline"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Website URL</label>
                  <Input
                    value={profileForm.website_url || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, website_url: e.target.value })}
                    placeholder="https://yourwebsite.com"
                    className="input-underline"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={() => setShowProfileModal(false)} className="btn-outline-gold flex-1 py-3">
                    Cancel
                  </Button>
                  <Button onClick={saveProfile} className="btn-gold flex-1 py-3">
                    Save Profile
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WomenConnect;
